/**
 * Navigation Utility Functions
 * Provides functions to generate absolute URLs for cross-subdomain navigation
 */

/**
 * Get the main site URL
 * Returns the main site URL (wesplit.io) or current host if already on main site
 * @returns {string} Main site URL
 */
export const getMainSiteUrl = () => {
  if (typeof window === 'undefined') return 'https://wesplit.io';
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // If already on main site (not docs, help, or admin subdomain), use current host
  if (!hostname.includes('doc.') && 
      !hostname.includes('help.') &&
      !hostname.includes('admin.') && 
      !hostname.includes('doc-site') && 
      !hostname.includes('help-site') &&
      !hostname.includes('admin-site') &&
      hostname !== 'doc.wesplit.io' &&
      hostname !== 'help.wesplit.io' &&
      hostname !== 'admin.wesplit.io') {
    return `${protocol}//${hostname}`;
  }
  
  // On localhost, return localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}`;
  }
  
  // Otherwise, return production main site
  return 'https://wesplit.io';
};

/**
 * Get the docs subdomain URL
 * Returns the docs subdomain URL (doc.wesplit.io) or current host if already on docs
 * @returns {string} Docs subdomain URL
 */
export const getDocsSiteUrl = () => {
  if (typeof window === 'undefined') return 'https://doc.wesplit.io';
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // If already on docs subdomain, use current host
  if (hostname.includes('doc.') || 
      hostname.includes('doc-site') ||
      hostname === 'doc.wesplit.io' ||
      hostname === 'doc-site-wesplit-35186.web.app') {
    return `${protocol}//${hostname}`;
  }
  
  // On localhost, return localhost (docs accessible via /api-docs routes)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}`;
  }
  
  // Otherwise, return production docs subdomain
  return 'https://doc.wesplit.io';
};

/**
 * Get the help subdomain URL
 * Returns the help subdomain URL (help.wesplit.io) or current host if already on help
 * @returns {string} Help subdomain URL
 */
export const getHelpSiteUrl = () => {
  if (typeof window === 'undefined') return 'https://help.wesplit.io';
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // If already on help subdomain, use current host
  if (hostname.includes('help.') || 
      hostname.includes('help-site') ||
      hostname === 'help.wesplit.io' ||
      hostname === 'help-site-wesplit-35186.web.app') {
    return `${protocol}//${hostname}`;
  }
  
  // On localhost, return localhost (help accessible via /help routes)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}`;
  }
  
  // Otherwise, return production help subdomain
  return 'https://help.wesplit.io';
};

/**
 * Get the admin subdomain URL
 * Returns the admin subdomain URL (admin.wesplit.io) or current host if already on admin
 * @returns {string} Admin subdomain URL
 */
export const getAdminSiteUrl = () => {
  if (typeof window === 'undefined') return 'https://admin.wesplit.io';
  
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // If already on admin subdomain, use current host
  if (hostname.includes('admin.') || 
      hostname.includes('admin-site') ||
      hostname === 'admin.wesplit.io' ||
      hostname === 'admin-site-wesplit-35186.web.app') {
    return `${protocol}//${hostname}`;
  }
  
  // On localhost, return localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}`;
  }
  
  // Otherwise, return production admin subdomain
  return 'https://admin.wesplit.io';
};

/**
 * Check if we're on the admin subdomain
 * This will be true when accessing admin.wesplit.io or the Firebase admin site
 * Checks exact matches first, then Firebase domains, then prefix patterns
 * Supports ?admin=true query parameter for local preview
 * @returns {boolean} True if on admin subdomain
 */
export const isAdminSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Allow ?admin=true query parameter for local preview
  if (searchParams.get('admin') === 'true') {
    return true;
  }
  
  // On localhost, always use main app (admin accessible via /admin routes)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  
  // Check exact matches first
  if (hostname === 'admin.wesplit.io' || 
      hostname === 'admin-site-wesplit-35186.web.app') {
    return true;
  }
  
  // Check Firebase default domain
  if (hostname.includes('admin-site')) {
    return true;
  }
  
  // Finally check prefix pattern (but only if not already matched)
  if (hostname.startsWith('admin.')) {
    return true;
  }
  
  return false;
};

/**
 * Check if we're on the docs subdomain
 * This will be true when accessing doc.wesplit.io or the Firebase docs site
 * On localhost, docs are accessible via /api-docs routes in main app
 * Checks exact matches first, then Firebase domains, then prefix patterns
 * Supports ?docs=true query parameter for local preview
 * @returns {boolean} True if on docs subdomain
 */
export const isDocsSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Allow ?docs=true query parameter for local preview
  if (searchParams.get('docs') === 'true') {
    return true;
  }
  
  // On localhost, always use main app (docs accessible via /api-docs routes)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  
  // Check exact matches first
  if (hostname === 'doc.wesplit.io' || 
      hostname === 'doc-site-wesplit-35186.web.app') {
    return true;
  }
  
  // Check Firebase default domain
  if (hostname.includes('doc-site')) {
    return true;
  }
  
  // Finally check prefix pattern (but only if not already matched)
  if (hostname.startsWith('doc.')) {
    return true;
  }
  
  return false;
};

/**
 * Check if we're on the help subdomain
 * This will be true when accessing help.wesplit.io or the Firebase help site
 * On localhost, help is accessible via /help routes in main app
 * Checks exact matches first, then Firebase domains, then prefix patterns
 * Supports ?help=true query parameter for local preview
 * @returns {boolean} True if on help subdomain
 */
export const isHelpSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Allow ?help=true query parameter for local preview
  if (searchParams.get('help') === 'true') {
    return true;
  }
  
  // On localhost, always use main app (help accessible via /help routes)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }
  
  // Check exact matches first
  if (hostname === 'help.wesplit.io' || 
      hostname === 'help-site-wesplit-35186.web.app') {
    return true;
  }
  
  // Check Firebase default domain
  if (hostname.includes('help-site')) {
    return true;
  }
  
  // Finally check prefix pattern (but only if not already matched)
  if (hostname.startsWith('help.')) {
    return true;
  }
  
  return false;
};

/**
 * Check if currently on docs subdomain (alias for isDocsSubdomain for backward compatibility)
 * @deprecated Use isDocsSubdomain() instead
 * @returns {boolean} True if on docs subdomain
 */
export const isOnDocsSubdomain = () => isDocsSubdomain();

/**
 * Check if currently on help subdomain (alias for isHelpSubdomain for backward compatibility)
 * @deprecated Use isHelpSubdomain() instead
 * @returns {boolean} True if on help subdomain
 */
export const isOnHelpSubdomain = () => isHelpSubdomain();

/**
 * Check if currently on main site
 * @returns {boolean} True if on main site (not docs, help, or admin subdomain)
 */
export const isOnMainSite = () => {
  if (typeof window === 'undefined') return true;
  return !isDocsSubdomain() && 
         !isHelpSubdomain() &&
         !isAdminSubdomain();
};

/**
 * Get the admin route - clean URL on admin subdomain, full path on main site
 * @param {string} route - Full route path (e.g., '/admin/dashboard')
 * @returns {string} Clean route on admin subdomain, full route on main site
 */
export const getAdminRoute = (route) => {
  if (isAdminSubdomain()) {
    // Remove /admin prefix and handle root
    const cleanRoute = route.replace('/admin', '') || '/';
    return cleanRoute;
  }
  return route;
};

/**
 * Get the API docs route - clean URL on docs subdomain, full path on main site
 * @param {string} route - Full route path (e.g., '/api-docs/overview')
 * @returns {string} Clean route on docs subdomain, full route on main site
 */
export const getApiDocsRoute = (route) => {
  if (isDocsSubdomain()) {
    // Remove /api-docs prefix and handle root
    const cleanRoute = route.replace('/api-docs', '') || '/';
    return cleanRoute;
  }
  return route;
};

/**
 * Get the help center route - clean URL on help subdomain, full path on main site
 * @param {string} route - Full route path (e.g., '/help/getting-started')
 * @returns {string} Clean route on help subdomain, full route on main site
 */
export const getHelpRoute = (route) => {
  if (isHelpSubdomain()) {
    // Remove /help prefix and handle root
    const cleanRoute = route.replace('/help', '') || '/';
    return cleanRoute;
  }
  return route;
};
